import { WarningBar } from 'components/Basic/WarningBar/WarningBar';
import { LoyaltyClubEarned } from 'components/Blocks/LoyaltyClubEarned/LoyaltyClubEarned';
import { B2bProductPrice } from 'components/Blocks/Product/Price/B2bProductPrice';
import WatchDog from 'components/Blocks/Product/WatchDog/WatchDog';
import Spinbox from 'components/Forms/Spinbox/Spinbox';
import { AddToCartButtonPrimary } from 'components/Pages/ProductDetail/ProductDetailAddToCart/AddToCartButtonPrimary';
import { ClubPriceInfo } from 'components/Pages/ProductDetail/ProductDetailAddToCart/ClubPriceInfo';
import { PricePerUnitWidget } from 'components/Pages/ProductDetail/ProductDetailAddToCart/PricePerUnitWidget';
import { PromotionDuration } from 'components/Pages/ProductDetail/ProductDetailAddToCart/PromotionDuration';
import { VatAndTransportInfo } from 'components/Pages/ProductDetail/ProductDetailAddToCart/VatAndTransportInfo';
import { mapAddToCartPopupData } from 'connectors/cart/Cart';
import { useSettings } from 'connectors/Settings/Settings';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { isB2bDomain, isDomainLocale } from 'helpers/isDomainLocale';
import { useAddToCart } from 'hooks/cart/useAddToCart';
import { getSellingPriceByDomain, useFormatPrice } from 'hooks/formatting/useFormatPrice';
import { useCurrentUserData } from 'hooks/user/useCurrentUserData';
import { createPersooEventForAddToCart } from 'modules/persoo/persooEvents';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useRef } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { AvailabilityStatusEnum } from 'types/availability';
import { GtmListNameType, GtmMessageOriginType } from 'types/gtm';
import { PricingHistoryStrategy } from 'types/price';
import { ProductDetailType, VariantDetailType } from 'types/product';
import twMerge from 'utils/twMerge';

type ProductDetailAddToCartProps = {
    gtmListName: GtmListNameType;
    product: ProductDetailType | VariantDetailType;
    isMobile: boolean;
};

const addToCartPriceTwClass = 'leading-8 text-h2 text-red font-bold text-primary';

const ProductDetailAddToCart: FC<ProductDetailAddToCartProps> = ({ product, isMobile }) => {
    const testIdentifier = 'pages-productdetail-addtocart';

    const spinboxRef = useRef<HTMLInputElement | null>(null);
    const { t } = useTranslation();
    const { isUserLoggedIn } = useCurrentUserData();
    const isSpecialPrice = product.price.priceWithVat < product.prices.basic.priceWithVat;
    const { formatPriceNumber, formatPriceObject, formatPriceObjectForSR } = useFormatPrice();
    const [changeCartItemQuantity, fetching] = useAddToCart(GtmMessageOriginType.product, 'product');
    const domainConfig = useDomainConfig();
    const isBookingOnly = product.maximumOrderableQuantity <= 0;
    const { updateUserState } = usePersistStore((s) => s);
    const settings = useSettings();
    const isVatAndTransportInfoRequired = isDomainLocale(domainConfig.defaultLocale, 'de');

    const isAddToCartDisplayed =
        !product.isSellingDenied &&
        product.maximumOrderableQuantity > 0 &&
        (!isB2bDomain(domainConfig) || (product.b2bMinimumProductOrder ?? 0) <= product.maximumOrderableQuantity);
    const isWatchdogDisplayed =
        !isAddToCartDisplayed ||
        (isB2bDomain(domainConfig) && product.availability.status === AvailabilityStatusEnum.outOfStock);

    const isReferencePriceRequired =
        (settings?.pricingHistoryStrategy === PricingHistoryStrategy.allExceptVouchers &&
            !product.isElectronicGiftVoucher &&
            !product.isVoucher) ||
        (settings?.pricingHistoryStrategy === PricingHistoryStrategy.discountedOnly && isSpecialPrice);

    const onAddToCartHandler = useCallback(async () => {
        if (spinboxRef.current === null) {
            return;
        }

        const newCartItemValue =
            spinboxRef.current.valueAsNumber -
            (spinboxRef.current.valueAsNumber % (product.b2bMinimumProductOrder ?? 1));

        const addToCartResult = await changeCartItemQuantity(product.uuid, null, newCartItemValue, 'detail');

        spinboxRef.current!.valueAsNumber = product.b2bMinimumProductOrder ?? 1;
        const mappedAddToCartResult = mapAddToCartPopupData(addToCartResult, domainConfig.currencyCode);

        if (mappedAddToCartResult) {
            createPersooEventForAddToCart(mappedAddToCartResult.catalogNumber);
            updateUserState({
                addToCartPopupData: {
                    gtmListName: 'detail',
                    listIndex: 1,
                    product: mappedAddToCartResult,
                },
            });
        }
    }, [
        changeCartItemQuantity,
        domainConfig.currencyCode,
        updateUserState,
        product.uuid,
        product.b2bMinimumProductOrder,
    ]);

    return (
        <>
            <div className="mb-4 flex flex-col gap-5 lg:mb-3" data-testid={testIdentifier}>
                <div>
                    {isB2bDomain(domainConfig) ? (
                        <B2bProductPrice
                            b2bPaymentDiscount={product.b2bPaymentDiscount}
                            basePrice={product.prices.basic}
                            isUnitPrice={(product.b2bMinimumProductOrder ?? 0) > 1}
                            sellingPrice={product.price}
                            variant="detail"
                        />
                    ) : (
                        <>
                            <div
                                data-testid={testIdentifier + '-price'}
                                className={twMerge(
                                    addToCartPriceTwClass,
                                    isSpecialPrice &&
                                        ' text-h6 font-medium leading-5 text-primary line-through decoration-red',
                                )}
                            >
                                <div
                                    aria-label={formatPriceObjectForSR(product.price)}
                                    className={twMerge(isVatAndTransportInfoRequired && 'mb-2 flex items-end')}
                                >
                                    {formatPriceObject(product.prices.basic)}
                                    {isVatAndTransportInfoRequired && !isSpecialPrice && <VatAndTransportInfo />}
                                </div>
                                {!isSpecialPrice && !isUserLoggedIn && (
                                    <ClubPriceInfo
                                        isVatAndTransportInfoRequired
                                        price={product.price}
                                        prices={product.prices}
                                    />
                                )}
                            </div>
                            {!isSpecialPrice && product.referencePrice && isReferencePriceRequired && (
                                <div className="my-1 inline-block h-fit py-[6px] text-tiny leading-2">
                                    {`${t('Lowest price in the last 30 days')} `}
                                    {formatPriceNumber(product.referencePrice)}
                                </div>
                            )}
                            {isSpecialPrice && (
                                <>
                                    <div className="my-1 inline-block h-fit bg-red p-[0.3rem] text-small font-medium text-white">
                                        {`${t('You will save')} `}
                                        {formatPriceNumber(
                                            getSellingPriceByDomain(product.prices.basic, domainConfig) -
                                                getSellingPriceByDomain(product.price, domainConfig),
                                        )}
                                    </div>
                                    <div data-testid={testIdentifier + '-price-special'}>
                                        <div className="flex items-end">
                                            <div className={addToCartPriceTwClass}>
                                                {formatPriceObject(product.price)}
                                            </div>
                                            {isVatAndTransportInfoRequired && <VatAndTransportInfo />}
                                        </div>
                                        <PromotionDuration price={product.price} />
                                        {!isUserLoggedIn && (
                                            <div className={addToCartPriceTwClass}>
                                                <ClubPriceInfo
                                                    isVatAndTransportInfoRequired
                                                    price={product.price}
                                                    prices={product.prices}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {isSpecialPrice && product.referencePrice && isReferencePriceRequired && (
                        <div className="my-1 inline-block h-fit py-[6px] text-tiny leading-2">
                            {`${t('Lowest price in the last 30 days')} `}
                            {formatPriceNumber(product.referencePrice)}
                        </div>
                    )}
                    {product.pricePerUnit && <PricePerUnitWidget pricePerUnit={product.pricePerUnit} />}
                    <LoyaltyClubEarned className="!mb-0" pointsWithPrice={product.loyaltyClubPointsObtained} />
                </div>
                {isWatchdogDisplayed && <WatchDog productName={product.name} productUuid={product.uuid} />}
                {isAddToCartDisplayed && (
                    <div className="leading">
                        {(product.b2bMinimumProductOrder ?? 0) > 1 && (
                            <div className="pb-2">
                                {t('Minimum order quantity {{ count }} pc', { count: product.b2bMinimumProductOrder })}
                            </div>
                        )}
                        <div className="flex items-stretch">
                            <Spinbox
                                defaultValue={product.b2bMinimumProductOrder ?? 1}
                                id={product.uuid}
                                idSuffix="-detail"
                                max={product.maximumOrderableQuantity}
                                min={product.b2bMinimumProductOrder ?? 1}
                                multiple={product.b2bMinimumProductOrder ?? 1}
                                ref={spinboxRef}
                                step={product.b2bMinimumProductOrder ?? 1}
                                overstepMessage={{
                                    tooHigh: t('You cannot add more items'),
                                    onlyMultiplesOf: t('Only multiples of {{ multiple }}', {
                                        multiple: product.b2bMinimumProductOrder ?? 1,
                                    }),
                                }}
                            />
                            <div className="ml-5 w-[calc(100%-146px)] flex-shrink-0 lg:w-auto">
                                <AddToCartButtonPrimary
                                    buttonText={isMobile ? t('Add to cart') : t('Add to cart (long)')}
                                    isBookingOnly={isBookingOnly}
                                    isLoading={!isBookingOnly && fetching}
                                    testIdentifier={`${testIdentifier}-button`}
                                    isDisabled={
                                        isB2bDomain(domainConfig) &&
                                        (product.maximumOrderableQuantity === 0 ||
                                            (product.b2bMinimumProductOrder ?? 0) > product.maximumOrderableQuantity)
                                    }
                                    onClick={onAddToCartHandler}
                                />
                            </div>
                        </div>
                    </div>
                )}
                {product.isExcludedFromDelivery && !isB2bDomain(domainConfig) && (
                    <WarningBar>
                        {t(
                            'This product has shipping restrictions, so some shipping options in the next step may be limited.',
                        )}
                    </WarningBar>
                )}
                {product.isExcludedFromReservation && !isB2bDomain(domainConfig) && (
                    <WarningBar>{t('This product cannot be reserved in a store.')}</WarningBar>
                )}
            </div>
        </>
    );
};

export default ProductDetailAddToCart;
